import { computed } from "vue";
import { formatPercent } from "@/utils/format";
import { useDashboardTab } from "@/composables/admin/dashboard/useDashboardTab";
import { getOccupancy } from "@/services/dashboard/dashboard";
import { getSeasonName } from "@/composables/admin/dashboard/seasonUtils";

/**
 * Composable del panel de ocupación del dashboard.
 * Muestra gauge radial, línea por jornada y comparativa entre temporadas.
 *
 * @param {import('vue').Ref<number[]>} seasonIdsRef
 * @param {import('vue').Ref<Array>} seasonsRef
 * @returns {{
 *     data: import('vue').Ref<object|null>,
 *     loading: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     isMultiSeason: import('vue').ComputedRef<boolean>,
 *     gaugeValue: import('vue').ComputedRef<number>,
 *     perJornadaChartData: import('vue').ComputedRef<Array>,
 *     seasonComparisonData: import('vue').ComputedRef<Array>,
 *     seasonBreakdown: import('vue').ComputedRef<Array|null>,
 *     seasonDelta: import('vue').ComputedRef<number|null>,
 *     deltaClass: import('vue').ComputedRef<string>,
 *     refresh: () => Promise<void>,
 * }}
 */
export function useOccupancy(seasonIdsRef, seasonsRef) {
    const base = useDashboardTab(seasonIdsRef, seasonsRef, getOccupancy);

    /**
     * Valor de ocupación para el gauge.
     * Siempre muestra la primera temporada seleccionada.
     * La comparativa se ve en la columna y línea por jornada.
     */
    const gaugeValue = computed(() => {
        const entry = base.currentSeasonEntry.value;
        return Number(entry?.avg_occupancy_pct || 0);
    });

    /**
     * Datos para el gráfico de línea por jornada.
     * Single: [['Jornada', 'Ocupación'], [j1, val], ...]
     * Multi:  [['Jornada', 'S1', 'S2', ...], [j1, v1, v2, ...], ...]
     *
     * En multi: solo se incluyen temporadas CON per_jornada.
     * Google Charts no permite una serie completamente null.
     * La comparativa de medias se ve en el ColumnChart y la tabla.
     */
    const perJornadaChartData = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};

        if (base.isMultiSeason.value) {
            const entries = Object.entries(perSeason).filter(
                ([, entry]) => entry?.per_jornada?.length,
            );
            if (entries.length === 0) return [];

            const allLabels = new Set();
            const seasonData = {};
            for (const [seasonId, entry] of entries) {
                const name = getSeasonName(seasonsRef.value, seasonId);
                seasonData[name] = entry.per_jornada || [];
                for (const j of entry.per_jornada) {
                    allLabels.add(j.jornada);
                }
            }

            // Orden numérico para que las jornadas se muestren correctamente
            const sortedLabels = Array.from(allLabels).sort(
                (a, b) => Number(a) - Number(b),
            );
            const names = Object.keys(seasonData);
            const header = ["Jornada"];
            for (const name of names) {
                header.push(name);
                header.push({ type: 'string', role: 'tooltip' });
            }
            const rows = sortedLabels.map(label => {
                const row = [label];
                for (const name of names) {
                    const j = seasonData[name].find(j => j.jornada === label);
                    const val = j ? Number(j.occupancy_pct || 0) : null;
                    row.push(val);
                    const tooltip = val !== null
                        ? `Jornada ${label}\nOcupación: ${formatPercent(val)}`
                        : '';
                    row.push(tooltip);
                }
                return row;
            });
            return [header, ...rows];
        }

        // Single season: primera con per_jornada
        const allEntries = Object.entries(perSeason).filter(([, entry]) => entry);
        const firstWithData = allEntries.find(
            ([, entry]) => entry?.per_jornada?.length,
        );
        if (!firstWithData) return [];
        const [, entry] = firstWithData;
        const jornadas = entry.per_jornada || [];
        const header = ["Jornada", "Ocupación", { type: 'string', role: 'tooltip' }];
        const rows = jornadas.map(j => {
            const val = Number(j.occupancy_pct || 0);
            return [j.jornada, val, `Jornada ${j.jornada}\nOcupación: ${formatPercent(val)}`];
        });
        return [header, ...rows];
    });

    /**
     * Datos para la columna comparativa entre temporadas.
     * [['Temporada', 'Ocupación'], [name, val], ...]
     */
    const seasonComparisonData = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(([, entry]) => entry);
        if (entries.length < 2) return [];

        const header = ["Temporada", "Ocupación"];
        const rows = entries.map(([seasonId, entry]) => {
            return [
                getSeasonName(seasonsRef.value, seasonId),
                Number(entry.avg_occupancy_pct || 0),
            ];
        });
        return [header, ...rows];
    });

    /**
     * Desglose por temporada para tabla comparativa.
     */
    const seasonBreakdown = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(([, entry]) => entry);
        if (entries.length === 0) return null;

        return entries.map(([seasonId, entry]) => {
            return {
                season: getSeasonName(seasonsRef.value, seasonId),
                avgOccupancy: Number(entry.avg_occupancy_pct || 0),
                clubOccupancy: Number(entry.occupancy_pct || 0),
                clubName: entry.club_name || "",
                matchCount: entry.per_jornada?.length || 0,
            };
        });
    });

    /**
     * Diferencia (delta) entre temporadas cuando son exactamente 2.
     * Retorna null si no aplica.
     */
    const seasonDelta = computed(() => {
        const breakdown = seasonBreakdown.value;
        if (!breakdown || breakdown.length !== 2) return null;
        // última - primera: positivo = mejora
        return breakdown[1].avgOccupancy - breakdown[0].avgOccupancy;
    });

    /**
     * Clase de color para el delta según su valor.
     */
    const deltaClass = computed(() => {
        const delta = seasonDelta.value;
        if (delta === null) return '';
        if (delta === 0) return 'tw:text-gray-500';
        return delta > 0
            ? 'tw:text-green-600 tw:font-semibold'
            : 'tw:text-red-600 tw:font-semibold';
    });

    /** Opciones para línea de ocupación por jornada */
    const lineChartOptions = computed(() => ({
        legend: { position: "left" },
        chartArea: { width: "85%", height: "70%" },
        colors: ["#e30613"],
        vAxis: { minValue: 0, maxValue: 100, title: "%" },
        hAxis: {
            title: "Jornada",
            textStyle: { fontSize: 11 },
        },
        curveType: "function",
    }));

    /** Opciones para columna comparativa entre temporadas */
    const comparisonChartOptions = {
        legend: { position: "none" },
        chartArea: { width: "80%", height: "70%" },
        colors: ["#2563eb"],
        vAxis: { minValue: 0, maxValue: 100, title: "%" },
    };

    return {
        data: base.data,
        loading: base.loading,
        error: base.error,
        isMultiSeason: base.isMultiSeason,
        seasonEntries: base.seasonEntries,
        gaugeValue,
        perJornadaChartData,
        seasonComparisonData,
        seasonBreakdown,
        seasonDelta,
        deltaClass,
        lineChartOptions,
        comparisonChartOptions,
        refresh: base.refresh,
    };
}
